import { jsx as _jsx } from "react/jsx-runtime";
import NVL from '@neo4j-nvl/base';
import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BASIC_WRAPPER_ID } from '../utils/constants';
import { getMapDifferences, getNodeAttributeDifferences } from '../utils/graph-comparison';
import { useDeepCompareEffect } from '../utils/hooks';
/**
 *
 * A basic React wrapper that wraps the base {@link NVL} library within a React component.
 * It takes the class' arguments as properties, which are passed to the NVL constructor.
 * Any changes in properties will be reflected in the NVL instance by calling the corresponding methods.
 *
 * For examples, head to the {@link https://neo4j.com/docs/nvl/current/react-wrappers/#_basic_react_wrapper Basic React wrapper documentation page}.
 */
export const BasicNvlWrapper = memo(forwardRef(({ nodes, rels, layout, layoutOptions, nvlCallbacks = {}, nvlOptions = {}, positions = [], onInitializationError, ...nvlEvents }, ref) => {
    const nvlRef = useRef(null);
    useImperativeHandle(ref, () => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const nvlMethods = Object.getOwnPropertyNames(NVL.prototype);
        return nvlMethods.reduce((current, method) => ({
            ...current,
            [method]: (...args) => {
                if (nvlRef.current === null) {
                    return null;
                }
                // @ts-ignore suppress the type casting error on spreading
                return nvlRef.current[method](...args);
            }
        }), 
        // eslint-disable-next-line max-len
        // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter, @typescript-eslint/consistent-type-assertions
        {});
    });
    const containerRef = useRef(null);
    const [currentNodes, setCurrentNodes] = useState(nodes);
    const [currentRels, setCurrentRels] = useState(rels);
    useEffect(() => {
        let newNvl = null;
        if (containerRef.current !== null) {
            if (nvlRef.current === null) {
                const combinedOptions = { ...nvlOptions, layoutOptions };
                if (layout !== undefined) {
                    combinedOptions.layout = layout;
                }
                try {
                    newNvl = new NVL(containerRef.current, currentNodes, currentRels, combinedOptions, nvlCallbacks);
                    nvlRef.current = newNvl;
                    setCurrentRels(rels);
                    setCurrentNodes(nodes);
                }
                catch (e) {
                    if (typeof onInitializationError === 'function') {
                        onInitializationError(e);
                    }
                    else {
                        throw e;
                    }
                }
            }
        }
        return () => {
            newNvl?.destroy();
            nvlRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (nvlRef.current === null) {
            return;
        }
        const nodeChanges = getMapDifferences(currentNodes, nodes);
        const nodeDiff = getNodeAttributeDifferences(currentNodes, nodes);
        const relChanges = getMapDifferences(currentRels, rels);
        const graphIsUnchanged = nodeChanges.added.length === 0 &&
            nodeChanges.removed.length === 0 &&
            nodeDiff.length === 0 &&
            relChanges.added.length === 0 &&
            relChanges.removed.length === 0 &&
            relChanges.updated.length === 0;
        if (graphIsUnchanged) {
            return;
        }
        setCurrentRels(rels);
        setCurrentNodes(nodes);
        const nodesToAddAndUpdate = [...nodeChanges.added, ...nodeDiff];
        const relsToAddAndUpdate = [...relChanges.added, ...relChanges.updated];
        nvlRef.current.addAndUpdateElementsInGraph(nodesToAddAndUpdate, relsToAddAndUpdate);
        const relationshipsToRemove = relChanges.removed.map((r) => r.id);
        const nodesToRemove = nodeChanges.removed.map((n) => n.id);
        nvlRef.current.removeRelationshipsWithIds(relationshipsToRemove);
        nvlRef.current.removeNodesWithIds(nodesToRemove);
    }, [currentNodes, currentRels, nodes, rels]);
    useEffect(() => {
        const updatedLayout = layout ?? nvlOptions.layout;
        if (nvlRef.current === null || updatedLayout === undefined) {
            return;
        }
        nvlRef.current.setLayout(updatedLayout);
    }, [layout, nvlOptions.layout]);
    useDeepCompareEffect(() => {
        const updatedLayoutOptions = layoutOptions ?? nvlOptions?.layoutOptions;
        if (nvlRef.current === null || updatedLayoutOptions === undefined) {
            return;
        }
        nvlRef.current.setLayoutOptions(updatedLayoutOptions);
    }, [layoutOptions, nvlOptions.layoutOptions]);
    useEffect(() => {
        if (nvlRef.current === null || nvlOptions.useWebGL === undefined) {
            return;
        }
        nvlRef.current.setUseWebGLRenderer(nvlOptions.useWebGL);
    }, [nvlOptions.useWebGL]);
    useEffect(() => {
        if (nvlRef.current === null || nvlOptions.renderer === undefined) {
            return;
        }
        nvlRef.current.setRenderer(nvlOptions.renderer);
    });
    useEffect(() => {
        if (nvlRef.current === null || nvlOptions.disableWebGL === undefined) {
            return;
        }
        nvlRef.current.setDisableWebGL(nvlOptions.disableWebGL);
    }, [nvlOptions.disableWebGL]);
    useEffect(() => {
        if (nvlRef.current === null || positions.length === 0) {
            return;
        }
        nvlRef.current.setNodePositions(positions);
    }, [positions]);
    return _jsx("div", { id: BASIC_WRAPPER_ID, ref: containerRef, style: { height: '100%', outline: '0' }, ...nvlEvents });
}));
